/**
 * WC prepares the exact variant/quantity/selling-plan/attribution payload in
 * wishlistAddToCartCallback. TCS owns that add operation on drawer-enabled
 * wishlist pages. The paid WC post-add redirect callback is not used.
 * WC reassigns this public entrypoint on every render, so retain its assignment
 * and expose our handler only when this theme's wishlist + drawer are present.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const settings = document.querySelector('script[data-tcs-wishlist-cart]')?.dataset;
let vendorAdd;
let pending = false;

async function readJSON(url, options) {
  const response = await fetch(url, options);
  const data = await response.json();
  if (!response.ok || data.status >= 400)
    throw new Error(data.description || data.message || settings?.cartAddError);
  return data;
}

function showError(message) {
  let notice = document.querySelector('.tcs-wishlist-cart-status');
  if (!notice) {
    notice = document.createElement('p');
    notice.className = 'tcs-wishlist-cart-status';
    notice.setAttribute('role', 'alert');
    document.querySelector('#MainContent')?.prepend(notice);
  }
  notice.textContent = message;
}

async function addWishlistItems(items) {
  // WC's renderer clears its own loading state immediately after calling this
  // entrypoint. Guard the entire transaction so a double click can't add twice.
  if (pending) return;
  pending = true;
  const origin = document.activeElement;
  const page = document.querySelector('.wishlistClubPage');
  const buttons = [...(page?.querySelectorAll('.wcButton') ?? [])];
  const disabled = buttons.map((button) => button.disabled);
  page?.setAttribute('aria-busy', 'true');
  buttons.forEach((button) => {
    button.disabled = true;
  });
  document.querySelector('.tcs-wishlist-cart-status')?.remove();
  let added = false;
  try {
    const sectionId = document.querySelector('#cart-drawer cart-items-component')?.dataset
      .sectionId;
    if (!sectionId) throw new Error(settings?.cartAddError);
    await customElements.whenDefined('cart-drawer-component');
    await customElements.whenDefined('cart-items-component');
    // The /cart template intentionally omits the drawer. Render its section
    // from the locale's storefront root, including on WC's app-proxy page.
    const sectionPath = Theme.routes.cart_url.replace(/\/cart\/?$/, '/');
    const result = await readJSON(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionId, sections_url: sectionPath }),
    });
    added = true;
    const cart = await readJSON(`${Theme.routes.cart_url}.js`, { cache: 'no-store' });
    // Shopify can commit the add but fail to render a section. Refetch only the
    // section in that case; never retry the mutation (which would add twice).
    let sections = result.sections;
    if (!sections?.[sectionId]) {
      const url = new URL(sectionPath, window.location.origin);
      url.searchParams.set('sections', sectionId);
      sections = await readJSON(url, { cache: 'no-store' });
    }
    if (!sections?.[sectionId]) throw new Error(settings?.cartRefreshError);
    const update = {
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        itemCount: cart.item_count,
        sections,
        source: 'tcs-wishlist-cart',
        didError: false,
      },
    };
    // Horizon owns drawer hydration, auto-open, badge count and announcements.
    // Dispatch only after success, with the authoritative cart and sections.
    page.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: Promise.resolve(update),
      }),
    );
  } catch (error) {
    showError(added ? settings?.cartRefreshError : error.message || settings?.cartAddError);
  } finally {
    page?.removeAttribute('aria-busy');
    buttons.forEach((button, index) => {
      button.disabled = disabled[index];
    });
    if (origin?.isConnected && !origin.disabled && document.activeElement === document.body)
      origin.focus({ preventScroll: true });
    pending = false;
  }
}

const descriptor = Object.getOwnPropertyDescriptor(window, 'wishlistAddToCartCallback');
if (!descriptor || descriptor.configurable) {
  vendorAdd = window.wishlistAddToCartCallback;
  Object.defineProperty(window, 'wishlistAddToCartCallback', {
    configurable: true,
    enumerable: true,
    get() {
      return document.querySelector('.wishlistClubPage') && document.querySelector('#cart-drawer')
        ? addWishlistItems
        : vendorAdd;
    },
    set(callback) {
      vendorAdd = callback;
    },
  });
}
